import { Injectable } from '@angular/core';
import {
    IFareComponent, IGetBookingsAirResponse, IGetBookingsStayResponse,
    IPrice,
    IRule,
    IRulePenalty,
    ISectorAir,
    ISectorStay,
    ITraveller,
    IXId,
    IXRole,
    TGetBookingResponse
} from "libraries/service/resources/booking/interfaces/IGetBookings";
import {
    ConditionColumns,
    ICondition,
    IConditionLine,
    IConditionLuggage,
    IConditionPricingValidity,
    IConditionSector,
    RuleType,
} from "./ICondition";
import { CancellationConditionsService } from "libraries/hotel/components/cancellation-conditions/services/cancellation-conditions.service";
import { BookingType } from "libraries/service/resources/booking/interfaces/get-bookings.enum";


@Injectable()
export class ConditionsTabService {

    constructor(
        private readonly cancellationConditionsService: CancellationConditionsService
    ) {
    }

    public computeConditionTableData(booking: TGetBookingResponse): ICondition {

        const rules: IRule[][] = getPriceRules(booking);

        return {
            columns: setConditionColumns(),
            lines: this.getLines(rules, booking)
        };
    }

    private getLines(rules: IRule[][], booking: TGetBookingResponse): IConditionLine[] {
        return rules
            .filter((rulesList: IRule[]) => Boolean(rulesList[0].penalty && rulesList[0].penalty[0].currency && rulesList[0].penalty[0].unitAmount))
            .map((rulesList: IRule[]) => {
                let relatedPrice: IPrice;
                if (booking.price && rulesList[0].price) {
                    relatedPrice = booking.price
                        .find((price: IPrice) => price.xId === rulesList[0].price.xId);
                } else {
                    relatedPrice = booking.price[0];
                }

                let relatedFareComponent: IFareComponent;
                if (booking.fareComponent && rulesList[0].price) {
                    relatedFareComponent = booking.fareComponent
                        .find((fareComponent: IFareComponent) => fareComponent.price.xId === rulesList[0].price.xId);
                }

                const ruleType: RuleType[] = [];
                const cancellation: IRulePenalty[] = booking.type[0] === BookingType.air ?
                    getRulePenalty(rulesList, RuleType.cancellation) :
                    (
                        this.cancellationConditionsService.getPenaltiesForAccomodation(
                            booking as IGetBookingsStayResponse, rulesList[0].accomodation.xId
                        ) as IRulePenalty[]
                    );
                const exchange: IRulePenalty[] = getRulePenalty(rulesList, RuleType.exchange);
                const luggage: IConditionLuggage[] = rulesList
                    .filter((rule: IRule) => rule.xType === RuleType.luggage)
                    .map((rule: IRule) => {
                        const ruleData: IConditionLuggage = {
                            in: rule.in,
                            restriction: rule.restriction,
                            quantity: rule.quantity,
                        };

                        if (rule.text) {
                            ruleData.text = rule.text;
                        }

                        return ruleData;
                    });

                rulesList.forEach((rule: IRule) => {
                    if (rule.xType === RuleType.cancellation || rule.xType === RuleType.exchange) {
                        ruleType.push(rule.xType);
                    }
                });

                const fareType: string[] = rulesList[0].price && relatedPrice && relatedPrice.rate
                    ? relatedPrice.rate.map((rate: IXRole) => rate.xRole)
                    : null;

                return {
                    // @ts-ignore
                    whenLastToCancelWithoutPenalty: booking.whenLastToCancelWithoutPenalty,
                    sector: getConditionSector(rulesList[0], (booking as IGetBookingsStayResponse | IGetBookingsAirResponse).sector),
                    traveller: getConditionTraveller(rulesList[0], booking.traveller),
                    pricing: {
                        validity: getPricingValidity(relatedPrice),
                        fareBasis: relatedFareComponent && relatedFareComponent.fareBasis ? relatedFareComponent.fareBasis.code : null,
                        fareFamily: relatedFareComponent && relatedFareComponent.fareFamily ? relatedFareComponent.fareFamily.name : null,
                        company: relatedPrice && relatedPrice.vendor ? relatedPrice.vendor.code : null,
                        fareType,
                        keywords: this.cancellationConditionsService.extractCancellationKeyword(booking.price),
                        total: relatedPrice && relatedPrice.total.total
                    },
                    condition: {
                        cancellation,
                        exchange,
                        luggage,
                    }
                };
            });
    }
}

function setConditionColumns(): ConditionColumns[] {
    return [
        ConditionColumns.sector,
        ConditionColumns.traveller,
        ConditionColumns.pricing,
        ConditionColumns.condition,
    ];
}

function getPriceRules(booking: TGetBookingResponse): IRule[][] {
    const pricesRules: Map<string, IRule[]> = new Map();

    booking.price && booking.price.forEach((price: IPrice) => {
        booking.rule && booking.rule.forEach((rule: IRule) => {
            if (rule.sector) {

                const keyPart1: string = price.xId;
                const keyPart2: string = rule.sector ?
                    rule.sector
                        .map((sector: IXId) => sector.xId)
                        .join('') :
                    '';

                const keyPart3: string = rule.traveller ?
                    rule.traveller
                        .map((traveller: IXId) => traveller.xId)
                        .join('') :
                    '';

                const key: string = `${keyPart1}_${keyPart2}_${keyPart3}`;
                if (pricesRules.has(key)) {
                    pricesRules.get(key).push(rule);
                } else {
                    pricesRules.set(key, [rule]);
                }
            }
        });
    });

    return Array.from(pricesRules.values());
}


function getConditionSector(rule: IRule, sectors: Array<ISectorStay | ISectorAir>): IConditionSector | null {

    if (rule.sector) {
        const data: Array<ISectorAir | ISectorStay> = rule.sector
            .map((sectorId: IXId) => sectors.find((sector: ISectorStay | ISectorAir) => sector.xId === sectorId.xId));
        return {
            type: data[0].xType,
            data
        };
    }

    return null;
}

function getConditionTraveller(rule: IRule, travellers: ITraveller[]): ITraveller[] | null {

    if (rule.traveller) {
        return rule.traveller
            .map((travellerId: IXId) => travellers.find((traveller: ITraveller) => traveller.xId === travellerId.xId));
    }

    return null;
}

function getRulePenalty(rulesList: IRule[], ruleType: RuleType): IRulePenalty[] | null {

    const ruleData: IRule = rulesList.find((rule: IRule) => rule.xType === ruleType);
    if (ruleData) {
        return ruleData.penalty;
    }
    return null;
}

function getPricingValidity(relatedPrice: IPrice): IConditionPricingValidity | null {

    return relatedPrice && relatedPrice.validity ? {
        dates: relatedPrice.validity,
        valid: new Date() <= new Date(relatedPrice.validity.end)
    } : null;
}


